import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { MapPin, Waves, Building2, Plane, ShoppingBag, Sailboat } from "lucide-react";

const ICONS = [Waves, Building2, Plane, ShoppingBag, Sailboat];

export default function Location() {
  const { t } = useLang();

  return (
    <section id="location" data-testid="location-section" className="py-24 md:py-32 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="md:col-span-5"
        >
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C48E66] mb-6" data-testid="location-eyebrow">{t.location.eyebrow}</p>
          <h2 className="font-serif text-5xl md:text-6xl text-[#112A46] font-light leading-[1.02] tracking-tight" data-testid="location-title">
            {t.location.title_1}<span className="italic">{t.location.title_italic}</span>
          </h2>
          <p className="mt-6 text-[#5C665F] text-base md:text-lg leading-relaxed">{t.location.subtitle}</p>

          <ul className="mt-10 space-y-6">
            {t.location.items.map((item, i) => {
              const Icon = ICONS[i] || MapPin;
              return (
                <li key={i} className="flex items-start gap-5 pb-6 border-b border-[#E2DFD2]" data-testid={`location-item-${i}`}>
                  <div className="mt-1">
                    <Icon size={22} strokeWidth={1} className="text-[#112A46]" />
                  </div>
                  <div>
                    <div className="font-serif text-2xl text-[#112A46]">{item.label}</div>
                    <div className="text-sm text-[#5C665F] mt-1 font-light">{item.value}</div>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="mt-8 flex items-center gap-3 text-sm text-[#1C2321]">
            <MapPin size={16} strokeWidth={1.25} className="text-[#C48E66]" />
            <span className="font-light" data-testid="location-address">{t.location.address}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="md:col-span-7"
        >
          <div className="relative aspect-[4/5] md:aspect-[4/5] overflow-hidden border border-[#E2DFD2]">
            <iframe
              title="Vila Chã map"
              data-testid="location-map"
              src="https://maps.google.com/maps?q=R.%20da%20Praia%20421%2C%204485-691%20Vila%20Ch%C3%A3%2C%20Vila%20do%20Conde&t=&z=17&ie=UTF8&iwloc=&output=embed"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{ border: 0 }}
              allowFullScreen
            />
          </div>
          <a
            href="https://www.google.com/maps/search/?api=1&query=R.+da+Praia+421%2C+4485-691+Vila+Ch%C3%A3%2C+Vila+do+Conde"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="location-map-link"
            className="mt-4 inline-block text-[11px] uppercase tracking-[0.25em] text-[#112A46] underline decoration-[#C48E66] underline-offset-4 hover:text-[#C48E66] transition-colors"
          >
            Ver mapa ampliado ↗
          </a>
        </motion.div>
      </div>
    </section>
  );
}
